import { AppStore } from "@/store/AppStore";
import {
  SearchEngineRequestFacetFilter,
  SearchEngineRequestFacetFilterContainsMediaKind,
  SearchEngineRequestFacetFilterCreatedBy,
  SearchEngineRequestFacetFilterInCollection,
  SearchEngineRequestFacetFilterKind,
  SearchEngineRequestFacetFilterModifiedBy,
  SearchEngineRequestFilter,
} from "@/store/pages/SearchPageStore/types";
import {
  SearchEngineParams,
  SupportedSearchSortByValue,
} from "@/modules/url-params/search-engine-params/types";
import { urlParamsModule } from "@/modules/url-params";
import {
  action,
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
  reaction,
  runInAction,
} from "mobx";
import { getContactsFacetFilterItems } from "@/modules/filters/common/getContactsFacetFilterItems";
import { ContactsFacetFilterItem } from "@/components/filters/contacts-facet-filter";
import { CollectionSearchReturnType } from "@/store/collections/types";
import { SortByKind } from "@/modules/lenses/types";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { CollectionsFacetFilterItem } from "@/components/filters/collections-facet-filter";
import { DateFacetFilterItem, RangeValue } from "@/components/filters/date-facet-filter";
import { getSelectedCollectionTitles } from "@/modules/filters/common/getSelectedCollectionTitles";
import { MediaKindsFacetFilterItem } from "@/components/filters/media-kinds-facet-filter";
import { getMediaKindsFacetFilterItems } from "@/modules/filters/common/getMediaKindsFacetFilterItems";
import { NoteMediaKind, NotesFilterOlderThan } from "@/store/note";
import { CalendarDate } from "@internationalized/date";
import { To } from "react-router";
import { isString } from "lodash-es";

export type SearchPageDateParams = { olderThan?: NotesFilterOlderThan; from?: string; to?: string };

type SearchPageFilterNavigationHandler = (searchParams: SearchEngineParams) => void;

export class SearchPageFilter {
  tabPath: To | undefined = undefined;
  createdBySearchQuery: string = "";
  modifiedBySearchQuery: string = "";
  loadingCollections: boolean = false;
  collectionsSearchQuery: string = "";
  dateParams?: SearchPageDateParams;
  searchedAndLoadedCollections: CollectionObservable[] = [];

  constructor(
    private readonly store: AppStore,
    private readonly path?: To,
    private readonly options?: {
      navigateToSearch?: SearchPageFilterNavigationHandler;
    }
  ) {
    this.tabPath = path;
    makeObservable<this, "updateSearchEngineParams">(this, {
      tabPath: observable,
      tabSearchQuery: computed,
      setTabPath: action,

      params: computed,
      searchQuery: computed,
      updateSearchEngineParams: action,

      addFilter: action,
      removeFilter: action,
      addFacetFilter: action,
      removeFacetFilter: action,
      hasAnyFilters: computed,
      activeFiltersCount: computed,
      clearAllFilters: action,

      // SORTING
      sortOptions: computed,
      sortBy: computed,
      setSortBy: action,

      // COLLECTIONS FILTER
      searchedAndLoadedCollections: observable,
      collectionsSearchQuery: observable,
      loadingCollections: observable,
      collectionsFacetFilterItems: computed,
      collectionsParam: computed,
      selectedCollectionTitles: computed,
      setCollectionSearchQuery: action,
      clearCollectionSearchQuery: action,
      toggleCollectionItem: action,
      clearCollections: action,
      initializeCollectionsSearch: action,

      // MEDIA_KINDS FILTER
      mediaKindsParam: computed,
      selectedMediaKinds: computed,
      mediaKindsFacetFilterItems: computed,
      clearMediaKinds: action,
      toggleMediaKindItem: action,

      // MODIFIED_BY FILTER
      modifiedBySearchQuery: observable,
      modifiedByParam: computed,
      modifiedByFacetFilterItems: computed,
      setModifiedBySearchQuery: action,
      clearModifiedBySearchQuery: action,
      toggleModifiedByItem: action,
      clearModifiedBy: action,

      // CREATED_BY FILTER
      createdBySearchQuery: observable,
      createdByParam: computed,
      createdByFacetFilterItems: computed,
      setCreatedBySearchQuery: action,
      clearCreatedBySearchQuery: action,
      toggleCreatedByItem: action,
      clearCreatedBy: action,

      // DATE FILTER
      dateParams: observable,
      dateSelectedRange: computed,
      setDateFilter: action,
      clearDateFilter: action,
      dateFilterItems: computed,
    });

    onBecomeObserved(this, "collectionsFacetFilterItems", () => this.initializeCollectionsSearch());
  }

  setTabPath = (path: To) => {
    this.tabPath = path;
  };

  get tabSearchQuery(): string {
    if (!this.tabPath) return "";

    if (isString(this.tabPath)) {
      const queryIndex = this.tabPath.indexOf("?");
      if (queryIndex === -1) return "";
      return this.tabPath.substring(queryIndex);
    }

    return this.tabPath.search || "";
  }

  get params(): SearchEngineParams {
    const params = urlParamsModule.search.parse({ searchQueryStr: this.tabSearchQuery });
    return params;
  }

  get hasAnyFilters() {
    if (this.params.filters.length) {
      return true;
    }

    if (this.params.facetFilters.length) {
      return true;
    }

    if (this.dateParams) {
      return true;
    }

    return false;
  }

  get searchQuery(): string {
    return urlParamsModule.search.stringify({ searchParams: this.params });
  }

  addFilter(filter: SearchEngineRequestFilter) {
    this.updateSearchEngineParams({
      filters: [...this.params.filters, filter],
    });
  }

  removeFilter(filter: SearchEngineRequestFilter) {
    const filters = this.params.filters.filter(f => f !== filter);
    this.updateSearchEngineParams({ filters });
  }

  addFacetFilter(filter: SearchEngineRequestFacetFilter) {
    const facetFilters = this.params.facetFilters.filter(f => f.kind !== filter.kind);
    this.updateSearchEngineParams({
      facetFilters: [...facetFilters, filter],
    });
  }

  removeFacetFilter(filterKind: SearchEngineRequestFacetFilterKind) {
    const facetFilters = this.params.facetFilters.filter(f => f.kind !== filterKind);
    this.updateSearchEngineParams({ facetFilters });
  }

  private updateSearchEngineParams({ facetFilters, sortBy }: Partial<SearchEngineParams>) {
    const searchParams = {
      ...this.params,
    };

    if (facetFilters) {
      searchParams.facetFilters = facetFilters;
    }

    if (sortBy) {
      searchParams.sortBy = sortBy;
    }

    if (this.options?.navigateToSearch) {
      this.options.navigateToSearch(searchParams);
      return;
    }

    this.store.navigation.goToSearch({ searchParams, openInSameTab: true });
  }

  // SORTING
  get sortOptions(): SortByKind[] {
    return [SortByKind.Relevance, SortByKind.LastModified, SortByKind.LastCreated];
  }

  setSortBy = (sortBy: SortByKind) => {
    this.updateSearchEngineParams({ sortBy: sortBy as SupportedSearchSortByValue });
  };

  get sortBy(): SortByKind {
    return (this.params.sortBy || SortByKind.Relevance) as SortByKind;
  }

  // COLLECTIONS FILTER
  initializeCollectionsSearch() {
    reaction(
      () => [this.collectionsSearchQuery],
      async () => {
        runInAction(() => {
          this.loadingCollections = true;
        });

        const collectionIndexTuples = await this.store.collections.search({
          query: this.collectionsSearchQuery,
          limit: 100,
          sortBy: SortByKind.LastViewed,
          returns: CollectionSearchReturnType.CollectionIndexTuple,
        });

        const collections = await Promise.all(
          collectionIndexTuples.map(tuple => this.store.collections.getAsync(tuple[2]))
        ).then(results => results.filter(collection => !!collection));

        runInAction(() => {
          this.searchedAndLoadedCollections = collections;
          this.loadingCollections = false;
        });
      },
      { fireImmediately: true }
    );
  }

  get collectionsParam(): SearchEngineRequestFacetFilterInCollection | undefined {
    const filter = this.params.facetFilters.find(f => f.kind === "IN_COLLECTION");
    if (filter && "collection_ids" in filter.value) {
      return filter as SearchEngineRequestFacetFilterInCollection;
    }
    return undefined;
  }

  get collectionsFacetFilterItems(): CollectionsFacetFilterItem[] {
    return this.searchedAndLoadedCollections.map(collection => ({
      id: collection.id,
      title: collection.label,
      subtitle: collection.subtitle,
      isSelected: this.collectionsParam?.value.collection_ids.includes(collection.id),
      isVisible: true,
    }));
  }

  get selectedCollectionTitles(): string[] {
    return getSelectedCollectionTitles(
      this.store,
      this.collectionsParam?.value.collection_ids || []
    );
  }

  setCollectionSearchQuery = (value: string) => {
    this.collectionsSearchQuery = value;
  };

  clearCollectionSearchQuery = () => {
    this.collectionsSearchQuery = "";
  };

  toggleCollectionItem = (id: string) => {
    const selectedCollections = this.collectionsParam?.value.collection_ids || [];
    const newSelectedCollections = selectedCollections.includes(id)
      ? selectedCollections.filter(collectionId => collectionId !== id)
      : [...selectedCollections, id];
    this.addFacetFilter({
      kind: "IN_COLLECTION",
      value: { collection_ids: newSelectedCollections },
    });
  };

  clearCollections = () => {
    this.removeFacetFilter("IN_COLLECTION");
  };

  // MEDIA_KINDS FILTER
  get mediaKindsParam(): SearchEngineRequestFacetFilterContainsMediaKind | undefined {
    const filter = this.params.facetFilters.find(f => f.kind === "CONTAINS_MEDIA_KIND");
    if (filter && "media_kinds" in filter.value) {
      return filter as SearchEngineRequestFacetFilterContainsMediaKind;
    }
    return undefined;
  }

  get selectedMediaKinds(): NoteMediaKind[] {
    return (this.mediaKindsParam?.value.media_kinds as NoteMediaKind[]) || [];
  }

  get mediaKindsFacetFilterItems(): MediaKindsFacetFilterItem[] {
    return getMediaKindsFacetFilterItems(this.selectedMediaKinds);
  }

  toggleMediaKindItem = (kind: NoteMediaKind) => {
    const selectedMediaKinds = this.selectedMediaKinds;
    const newSelectedMediaKinds = selectedMediaKinds.includes(kind)
      ? selectedMediaKinds.filter(mediaKind => mediaKind !== kind)
      : [...selectedMediaKinds, kind];
    this.addFacetFilter({
      kind: "CONTAINS_MEDIA_KIND",
      value: { media_kinds: newSelectedMediaKinds },
    });
  };

  clearMediaKinds = () => {
    this.removeFacetFilter("CONTAINS_MEDIA_KIND");
  };

  // MODIFIED_BY FILTER
  get modifiedByParam(): SearchEngineRequestFacetFilterModifiedBy | undefined {
    const filter = this.params.facetFilters.find(f => f.kind === "MODIFIED_BY_SPACE_ACCOUNT");
    if (filter && "space_account_ids" in filter.value) {
      return filter as SearchEngineRequestFacetFilterModifiedBy;
    }
    return undefined;
  }

  get modifiedByFacetFilterItems(): ContactsFacetFilterItem[] {
    return getContactsFacetFilterItems(
      this.store,
      this.modifiedBySearchQuery,
      this.modifiedByParam?.value.space_account_ids || []
    );
  }

  setModifiedBySearchQuery = (value: string) => {
    this.modifiedBySearchQuery = value;
  };

  clearModifiedBySearchQuery = () => {
    this.modifiedBySearchQuery = "";
  };

  toggleModifiedByItem = (id: string) => {
    const selectedContacts = this.modifiedByParam?.value.space_account_ids || [];
    const newSelectedContacts = selectedContacts.includes(id)
      ? selectedContacts.filter(contactId => contactId !== id)
      : [...selectedContacts, id];

    this.addFacetFilter({
      kind: "MODIFIED_BY_SPACE_ACCOUNT",
      value: { space_account_ids: newSelectedContacts },
    });
  };

  clearModifiedBy = () => {
    this.removeFacetFilter("MODIFIED_BY_SPACE_ACCOUNT");
  };

  // CREATED_BY FILTER
  get createdByParam(): SearchEngineRequestFacetFilterCreatedBy | undefined {
    const filter = this.params.facetFilters.find(f => f.kind === "CREATED_BY_SPACE_ACCOUNT");
    if (filter && "space_account_ids" in filter.value) {
      return filter as SearchEngineRequestFacetFilterCreatedBy;
    }
    return undefined;
  }

  get createdByFacetFilterItems(): ContactsFacetFilterItem[] {
    return getContactsFacetFilterItems(
      this.store,
      this.createdBySearchQuery,
      this.createdByParam?.value.space_account_ids || []
    );
  }

  setCreatedBySearchQuery = (value: string) => {
    this.createdBySearchQuery = value;
  };

  clearCreatedBySearchQuery = () => {
    this.createdBySearchQuery = "";
  };

  toggleCreatedByItem = (id: string) => {
    const selectedContacts = this.createdByParam?.value.space_account_ids || [];
    const newSelectedContacts = selectedContacts.includes(id)
      ? selectedContacts.filter(contactId => contactId !== id)
      : [...selectedContacts, id];

    this.addFacetFilter({
      kind: "CREATED_BY_SPACE_ACCOUNT",
      value: { space_account_ids: newSelectedContacts },
    });
  };

  clearCreatedBy = () => {
    this.removeFacetFilter("CREATED_BY_SPACE_ACCOUNT");
  };

  // DATE FILTER
  get dateFilterItems(): DateFacetFilterItem[] {
    return [
      {
        type: "any",
        title: "Any date",
        isSelected: !this.dateParams,
        onSelect: () => this.clearDateFilter(),
      },
      {
        type: "older-than",
        title: "Older than a week",
        isSelected: this.dateParams?.olderThan === "week",
        onSelect: () => this.setDateFilter({ olderThan: "week" }),
      },
      {
        type: "older-than",
        title: "Older than a month",
        isSelected: this.dateParams?.olderThan === "month",
        onSelect: () => this.setDateFilter({ olderThan: "month" }),
      },
      {
        type: "older-than",
        title: "Older than a year",
        isSelected: this.dateParams?.olderThan === "year",
        onSelect: () => this.setDateFilter({ olderThan: "year" }),
      },
      {
        type: "custom-range",
        title: "Custom date range",
        isSelected: !!this.dateParams?.from,
        onSelect: ({ from, to }) => {
          this.setDateFilter({
            olderThan: undefined,
            from: from?.toString(),
            to: to?.toString(),
          });
        },
      },
    ];
  }

  setDateFilter({ olderThan, from, to }: SearchPageDateParams) {
    this.dateParams = { olderThan, from, to };
  }

  clearDateFilter() {
    this.dateParams = undefined;
  }

  get dateSelectedRange(): RangeValue | undefined {
    if (!this.dateParams?.from || !this.dateParams?.to) {
      return undefined;
    }

    const splitFrom = this.dateParams.from.split("-");
    const from = new CalendarDate(Number(splitFrom[0]), Number(splitFrom[1]), Number(splitFrom[2]));

    const splitTo = this.dateParams.to.split("-");
    const to = new CalendarDate(Number(splitTo[0]), Number(splitTo[1]), Number(splitTo[2]));

    return {
      start: from,
      end: to,
    };
  }

  get activeFiltersCount() {
    return this.params.facetFilters.length + this.params.filters.length + (this.dateParams ? 1 : 0);
  }

  clearAllFilters = () => {
    this.updateSearchEngineParams({ facetFilters: [] });
    this.clearDateFilter();
  };
}
